import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { orderApi } from '../api';
import { Order, TrackingResult } from '../types';
import { toImageUrl } from '../utils/image';
import Loading from '../components/common/Loading';

type Action = 'cancel' | 'refund';

/** 유튜브 watch/youtu.be/embed 링크면 임베드 URL 반환, 아니면 null. */
const youtubeEmbed = (url: string): string | null => {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

const OrderDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [tracking, setTracking] = useState<TrackingResult | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    orderApi.getOne(Number(id))
      .then(r => setOrder(r.data.data))
      .finally(() => setLoading(false));
  }, [id]);

  const openTracking = async () => {
    if (!order) return;
    setTrackingOpen(true);
    setTracking(null);
    setTrackingLoading(true);
    try {
      const res = await orderApi.getTracking(order.id);
      setTracking(res.data.data);
    } catch (err: any) {
      setTracking({ trackingNumber: null, latestStatus: null, checkpoints: null,
        error: err.response?.data?.message || '조회 실패' });
    } finally {
      setTrackingLoading(false);
    }
  };

  if (loading) return <Loading />;
  if (!order) return <div style={s.empty}>{t('orderDetail.notFound')}</div>;

  const canCancel = order.status === 'PENDING' || order.status === 'PAID';
  const canRefund = order.status === 'DELIVERED';

  const handleSubmitAction = async () => {
    if (!order) return;
    if (action === 'refund' && reason.trim().length === 0) {
      alert(t('orderDetail.refundReasonPlaceholder'));
      return;
    }
    setSubmitting(true);
    try {
      if (action === 'cancel') {
        const r = await orderApi.cancel(order.id, reason.trim() || undefined);
        setOrder(r.data.data);
        alert(t('orderDetail.cancelSuccess'));
      } else {
        const r = await orderApi.requestRefund(order.id, reason.trim());
        setOrder(r.data.data);
        alert(t('orderDetail.refundSuccess'));
      }
      setAction(null);
      setReason('');
    } catch (err: any) {
      alert(err.response?.data?.message ||
        t(action === 'cancel' ? 'orderDetail.cancelFailed' : 'orderDetail.refundFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <Link to="/mypage" style={s.back}>{t('orderDetail.backToList')}</Link>
      </div>

      <h1 style={s.title}>{t('orderDetail.title')}</h1>

      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={s.orderId}>{t('mypage.orderNumber')}{order.id}</span>
          <span style={s.date}>{order.createdAt.slice(0, 10)}</span>
          <span style={s.status}>{t(`orderStatus.${order.status}`)}</span>
        </div>

        {order.packingVideoUrl && (
          <div style={s.section}>
            <h3 style={s.sectionTitle}>📦 {t('orderDetail.packingVideo')}</h3>
            {youtubeEmbed(order.packingVideoUrl) ? (
              <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>
                <iframe
                  title="packing video"
                  src={youtubeEmbed(order.packingVideoUrl)!}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <a href={order.packingVideoUrl} target="_blank" rel="noopener noreferrer"
                 style={{ ...s.trackingBtn, display: 'inline-block', marginTop: 8, textDecoration: 'none' }}>
                ▶ {t('orderDetail.packingVideoView')} ↗
              </a>
            )}
          </div>
        )}

        {order.trackingNumber && (
          <div style={s.section}>
            <div style={s.trackingHeader}>
              <h3 style={s.sectionTitle}>{t('orderDetail.tracking')}</h3>
              <button type="button" style={s.trackingBtn} onClick={openTracking}>
                🚚 {t('orderDetail.trackingView')}
              </button>
            </div>
            <div style={s.infoGrid}>
              <div style={s.infoRow}>
                <span style={s.infoLabel}>{t('orderDetail.courier')}</span>
                <span>우체국 (Korea Post)</span>
              </div>
              <div style={s.infoRow}>
                <span style={s.infoLabel}>{t('orderDetail.trackingNumber')}</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{order.trackingNumber}</span>
              </div>
            </div>
          </div>
        )}

        <div style={s.section}>
          <h3 style={s.sectionTitle}>{t('orderDetail.orderProducts')}</h3>
          {(order.items ?? order.orderItems ?? []).map(item => (
            <div key={item.orderItemId ?? item.productId} style={s.item}>
              <img src={toImageUrl(item.thumbnailUrl)} alt="" style={s.itemImg} />
              <div style={{ flex: 1 }}>
                <p style={s.itemName}>{item.name ?? (item as any).productName}</p>
                <p style={s.itemMeta}>{item.quantity} × ${item.unitPrice.toFixed(2)}</p>
              </div>
              <p style={s.itemTotal}>${(item.lineTotal ?? (item as any).totalPrice ?? 0).toFixed(2)}</p>
            </div>
          ))}
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>{t('orderDetail.delivery')}</h3>
          <div style={s.infoGrid}>
            <div style={s.infoRow}><span style={s.infoLabel}>{t('orderDetail.receiverName')}</span><span>{order.shippingAddress?.recipientName ?? '-'}</span></div>
            <div style={s.infoRow}><span style={s.infoLabel}>{t('orderDetail.receiverPhone')}</span><span>{order.shippingAddress?.phone ?? '-'}</span></div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>{t('orderDetail.address')}</span>
              <span>
                {order.shippingAddress
                  ? `[${order.shippingAddress.postalCode}] ${[order.shippingAddress.line1, order.shippingAddress.line2].filter(Boolean).join(' ')}, ${[order.shippingAddress.city, order.shippingAddress.state].filter(Boolean).join(', ')}`
                  : '-'}
              </span>
            </div>
          </div>
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>{t('orderDetail.payment')}</h3>
          <div style={s.infoGrid}>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>{t('orderDetail.productAmount')}</span>
              <span>${order.subtotal.toFixed(2)}</span>
            </div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>{t('orderDetail.shipping')}</span>
              <span>${order.shippingFee.toFixed(2)}</span>
            </div>
            {order.discountAmount > 0 && (
              <div style={s.infoRow}>
                <span style={s.infoLabel}>{t('orderDetail.couponDiscount')}</span>
                <span style={{ color: '#e53935' }}>−${order.discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div style={{ ...s.infoRow, borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 4 }}>
              <span style={{ ...s.infoLabel, fontWeight: 700, color: '#333' }}>{t('orderDetail.finalAmount')}</span>
              <span style={s.finalAmount}>${order.totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {(order.cancelledAt || order.refundRequestedAt) && (
          <div style={s.section}>
            <h3 style={s.sectionTitle}>
              {order.cancelledAt ? t('orderDetail.cancelReasonLabel') : t('orderDetail.refundReasonLabel')}
            </h3>
            <div style={s.infoGrid}>
              {order.cancelledAt && (
                <>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>{t('orderDetail.cancelledAt')}</span>
                    <span>{order.cancelledAt.slice(0, 10)}</span>
                  </div>
                  {order.cancelReason && (
                    <div style={s.infoRow}>
                      <span style={s.infoLabel}>{t('orderDetail.cancelReasonLabel')}</span>
                      <span>{order.cancelReason}</span>
                    </div>
                  )}
                </>
              )}
              {order.refundRequestedAt && (
                <>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>{t('orderDetail.refundRequestedAt')}</span>
                    <span>{order.refundRequestedAt.slice(0, 10)}</span>
                  </div>
                  {order.refundReason && (
                    <div style={s.infoRow}>
                      <span style={s.infoLabel}>{t('orderDetail.refundReasonLabel')}</span>
                      <span>{order.refundReason}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {(canCancel || canRefund) && (
          <div style={s.actions}>
            {canCancel && (
              <button style={s.cancelBtn} onClick={() => { setAction('cancel'); setReason(''); }}>
                {t('orderDetail.cancelBtn')}
              </button>
            )}
            {canRefund && (
              <button style={s.refundBtn} onClick={() => { setAction('refund'); setReason(''); }}>
                {t('orderDetail.refundBtn')}
              </button>
            )}
          </div>
        )}
      </div>

      {trackingOpen && (
        <div style={s.modalBackdrop} onClick={() => setTrackingOpen(false)}>
          <div style={{ ...s.modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{t('orderDetail.tracking')}</h3>
            <p style={s.trackingSubtitle}>
              우체국 · {order.trackingNumber}
              {tracking?.latestStatus && <strong style={{ marginLeft: 8, color: '#333' }}>{tracking.latestStatus}</strong>}
            </p>

            {trackingLoading ? (
              <p style={{ padding: 24, textAlign: 'center', color: '#888' }}>조회 중...</p>
            ) : tracking?.error ? (
              <p style={s.trackingError}>{tracking.error}</p>
            ) : tracking?.checkpoints && tracking.checkpoints.length > 0 ? (
              <div style={s.timeline}>
                {[...tracking.checkpoints].reverse().map((cp, i) => (
                  <div key={i} style={s.timelineItem}>
                    <div style={{ ...s.timelineDot, ...(i === 0 ? s.timelineDotActive : {}) }} />
                    <div style={s.timelineContent}>
                      <p style={s.timelineMessage}>{cp.status ?? cp.description}</p>
                      <p style={s.timelineMeta}>
                        {[cp.location, cp.description].filter(Boolean).join(' · ')}
                      </p>
                      {cp.dateTime && <p style={s.timelineTime}>{cp.dateTime}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                {t('orderDetail.trackingEmpty')}
              </p>
            )}

            <div style={s.uspsHint}>
              <span style={s.uspsHintText}>🇺🇸 {t('orderDetail.trackingUspsHint')}</span>
              <a
                style={s.uspsLink}
                href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(order.trackingNumber ?? '')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('orderDetail.trackingUspsLink')} ↗
              </a>
            </div>

            <div style={s.modalActions}>
              <button style={s.modalCancelBtn} onClick={() => setTrackingOpen(false)}>{t('orderDetail.trackingClose')}</button>
            </div>
          </div>
        </div>
      )}

      {action && (
        <div style={s.modalBackdrop} onClick={() => !submitting && setAction(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>
              {action === 'cancel' ? t('orderDetail.cancelTitle') : t('orderDetail.refundTitle')}
            </h3>
            <label style={s.modalLabel}>
              {action === 'cancel' ? t('orderDetail.cancelReason') : t('orderDetail.refundReason')}
            </label>
            <textarea
              style={s.modalTextarea}
              rows={4}
              placeholder={action === 'cancel'
                ? t('orderDetail.cancelReasonPlaceholder')
                : t('orderDetail.refundReasonPlaceholder')}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
            <div style={s.modalActions}>
              <button
                style={s.modalCancelBtn}
                onClick={() => setAction(null)}
                disabled={submitting}
              >{t('common.cancel')}</button>
              <button
                style={action === 'cancel' ? s.modalCancelConfirmBtn : s.modalRefundConfirmBtn}
                onClick={handleSubmitAction}
                disabled={submitting}
              >
                {submitting ? '...' : (action === 'cancel'
                  ? t('orderDetail.cancelConfirm')
                  : t('orderDetail.refundConfirm'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 720, margin: '40px auto', padding: '0 20px' },
  topBar: { marginBottom: 16 },
  back: { fontSize: 14, color: '#888' },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 24 },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#bbb' },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'hidden' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' },
  orderId: { fontWeight: 700, color: '#333', flex: 1 },
  date: { fontSize: 13, color: '#888' },
  status: { fontSize: 12, padding: '4px 12px', background: '#ece3f7', color: '#b39ddb', borderRadius: 12, fontWeight: 600 },
  section: { padding: '20px 24px', borderBottom: '1px solid #f8f8f8' },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 16 },
  item: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  itemImg: { width: 56, height: 56, objectFit: 'cover', borderRadius: 6, background: '#f5f5f5' },
  itemName: { fontSize: 13, fontWeight: 500, marginBottom: 2 },
  itemMeta: { fontSize: 12, color: '#888' },
  itemTotal: { fontSize: 14, fontWeight: 600 },
  infoGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  infoRow: { display: 'flex', gap: 16 },
  infoLabel: { width: 100, fontSize: 13, color: '#888', flexShrink: 0 },
  finalAmount: { fontSize: 18, fontWeight: 700, color: '#b39ddb' },
  actions: { padding: '16px 24px', display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#fafafa' },
  cancelBtn: {
    padding: '10px 20px', background: '#fff', color: '#e53935',
    border: '1px solid #e53935', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  refundBtn: {
    padding: '10px 20px', background: '#fff', color: '#555',
    border: '1px solid #ccc', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  modalBackdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
  },
  modal: {
    background: '#fff', borderRadius: 12, padding: 28, width: 420, maxWidth: '90%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
  },
  modalTitle: { fontSize: 17, fontWeight: 700, marginBottom: 16, color: '#333' },
  modalLabel: { display: 'block', fontSize: 13, color: '#555', marginBottom: 8 },
  modalTextarea: {
    width: '100%', padding: '10px 12px', border: '1px solid #e8e8e8',
    borderRadius: 8, fontSize: 14, resize: 'none', fontFamily: 'inherit', outline: 'none',
    boxSizing: 'border-box',
  },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
  modalCancelBtn: {
    padding: '10px 16px', background: '#f5f5f5', color: '#555',
    border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer',
  },
  modalCancelConfirmBtn: {
    padding: '10px 20px', background: '#e53935', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  modalRefundConfirmBtn: {
    padding: '10px 20px', background: '#b39ddb', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  trackingHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
  },
  trackingBtn: {
    padding: '6px 14px', background: '#fff', color: '#5c6bc0',
    border: '1px solid #5c6bc0', borderRadius: 6, fontSize: 12,
    fontWeight: 600, cursor: 'pointer',
  },
  trackingSubtitle: { fontSize: 12, color: '#888', marginBottom: 20 },
  trackingError: { padding: 24, textAlign: 'center', color: '#e53935' },
  timeline: { display: 'flex', flexDirection: 'column', maxHeight: 400, overflowY: 'auto' },
  timelineItem: { display: 'flex', gap: 12, paddingBottom: 16, position: 'relative' },
  timelineDot: {
    width: 10, height: 10, borderRadius: '50%', background: '#ddd',
    marginTop: 6, flexShrink: 0,
  },
  timelineDotActive: { background: '#b39ddb', boxShadow: '0 0 0 4px #f3ecfb' },
  timelineContent: { flex: 1, borderLeft: '1px solid #f0f0f0', paddingLeft: 12, marginLeft: -16, paddingTop: 0 },
  timelineMessage: { fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4 },
  timelineMeta: { fontSize: 11, color: '#888' },
  timelineTime: { fontSize: 11, color: '#bbb', marginTop: 2 },
  uspsHint: {
    display: 'flex', flexDirection: 'column', gap: 6, marginTop: 16, padding: '12px 14px',
    background: '#f5f7fb', borderRadius: 8,
  },
  uspsHintText: { fontSize: 12, color: '#666', lineHeight: 1.5 },
  uspsLink: { fontSize: 12, fontWeight: 600, color: '#3b5bbf', textDecoration: 'none', alignSelf: 'flex-start' },
};

export default OrderDetailPage;
